import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconType } from 'react-icons';
import {
  BiInfoCircle,
  BiSolidInbox,
  BiSolidMinusCircle,
  BiSolidPlusCircle,
  BiWallet,
} from 'react-icons/bi';
import { getLedger, getRevenue } from '@/api/finance.api';
import { ApiError } from '@/api/errors';
import { StatusChip } from '@/components/common/StatusChip';
import { DataTable, type DataTableColumn } from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import {
  DashboardFilterBar,
  type DashboardFilterBarFieldErrors,
} from '@/pages/dashboard/DashboardFilterBar';
import { useWidgetRetryFocus, type WidgetFetchStatus } from '@/lib/useWidgetRetryFocus';
import { formatAmountNumber, formatCurrencyEGP, formatDateOnly } from '@/lib/format';
import {
  FINANCE_DEFAULT_PERIOD,
  FINANCE_PERIODS,
  type FinancePeriod,
  type FinancePeriodQuery,
  type LedgerEntry,
  type RevenueResponse,
} from '@/types/shared';
import './FinanceOverviewTab.css';

/** Left-to-right isolate / pop-directional-isolate — the plain-text-safe bidi wrap for values
 * interpolated into an i18next `t()` string (mirrors `dashboardWidgets.ts`'s
 * `getScopedCardTitle` convention; a real `<bdi>` element isn't available inside a plain string
 * return value). */
function isolate(value: string): string {
  return `⁦${value}⁩`;
}

/** Every reversal contra leg shares this `entryType` regardless of which `direction` it carries
 * (17-finance-ux.md §3.5) — used both to badge the reversal rows themselves and, paired with
 * `reversalPairKey`, to also flag the original (now-superseded) reservation rows. */
function reversalPairKey(entry: LedgerEntry): string | null {
  if (!entry.relatedStudentCode || !entry.relatedExamSection) return null;
  return `${entry.relatedStudentCode}::${entry.relatedExamSection}`;
}

function computeReversedPairKeys(entries: LedgerEntry[]): Set<string> {
  const keys = new Set<string>();
  for (const entry of entries) {
    if (entry.entryType === 'exam_reversal') {
      const key = reversalPairKey(entry);
      if (key) keys.add(key);
    }
  }
  return keys;
}

function renderRelated(entry: LedgerEntry): ReactNode {
  const parts: ReactNode[] = [];
  if (entry.relatedStudentCode) {
    parts.push(
      <bdi key="code" className="fa-ltr-isolate">
        {entry.relatedStudentCode}
      </bdi>,
    );
  }
  if (entry.relatedExamSection) {
    parts.push(<span key="section">{entry.relatedExamSection}</span>);
  }
  if (entry.relatedSchoolLabel) {
    parts.push(<span key="school">{entry.relatedSchoolLabel}</span>);
  }
  if (entry.relatedPartnerLabel) {
    parts.push(<span key="partner">{entry.relatedPartnerLabel}</span>);
  }
  if (parts.length === 0) return '—';
  return parts.flatMap((part, idx) =>
    idx === 0 ? [part] : [<span key={`sep-${idx}`}> · </span>, part],
  );
}

interface LedgerAmountCellProps {
  entry: LedgerEntry;
  language: string;
}

/** Amount cell — always renders the positive EGP magnitude; for SAT/ACT rows (`originalCurrency
 * === 'USD'`) adds a keyboard-reachable expand toggle showing the original USD amount × rate
 * (17-finance-ux.md §3.4, "original-currency detail on demand"). A native `title` tooltip alone
 * isn't reliably keyboard/screen-reader accessible, so this is an inline expand, not a hover-only
 * affordance. */
function LedgerAmountCell({ entry, language }: LedgerAmountCellProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasOriginal = entry.originalCurrency != null && entry.originalAmount != null;

  return (
    <div className="fa-ledger-amount-cell">
      <span className="fa-ltr-isolate">
        <bdi>{formatCurrencyEGP(Number(entry.amount), language)}</bdi>
      </span>
      {hasOriginal && (
        <button
          type="button"
          className="fa-ledger-amount-cell__toggle"
          aria-expanded={expanded}
          aria-label={t('finance.ledger.originalAmountToggleAriaLabel')}
          onClick={() => setExpanded((v) => !v)}
        >
          <BiInfoCircle aria-hidden="true" />
        </button>
      )}
      {hasOriginal && expanded && (
        <p className="fa-ledger-amount-cell__detail">
          <bdi className="fa-ltr-isolate">
            {t('finance.ledger.originalAmountTooltip', {
              originalAmount: formatAmountNumber(Number(entry.originalAmount), language),
              originalCurrency: entry.originalCurrency,
              rate: entry.usdEgpRate ? formatAmountNumber(Number(entry.usdEgpRate), language) : '',
              egp: formatAmountNumber(Number(entry.amount), language),
            })}
          </bdi>
        </p>
      )}
    </div>
  );
}

interface FinanceRevenueSummaryProps {
  revenue: RevenueResponse | null;
  status: WidgetFetchStatus;
  onRetry: () => void;
}

interface RevenueCardDef {
  key: 'income' | 'expenses' | 'net';
  labelKey: string;
  icon: IconType;
  accent: 'green' | 'orange' | 'indigo';
  value: string | null;
}

/**
 * Revenue KPI row + breakdown (17-finance-ux.md §3.3) — reuses the dashboard KPI stat-card
 * anatomy (icon chip + label + big value) as fresh, page-local markup rather than the dashboard's
 * `StatCardWidget` component itself, since these cards are non-navigable (no `deepLink`) and the
 * "Net" card is the first real use of the reserved `--color-accent-indigo` slot
 * (`13-dashboard-restyle-spec.md §1.1`).
 */
function FinanceRevenueSummary({ revenue, status, onRetry }: FinanceRevenueSummaryProps) {
  const { t, i18n } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement>(null);
  const retriedRef = useRef(false);
  useWidgetRetryFocus(status, retriedRef, headingRef, retryButtonRef);

  function handleRetryClick() {
    retriedRef.current = true;
    onRetry();
  }

  const netNegative = revenue != null && Number(revenue.net) < 0;

  const cards: RevenueCardDef[] = [
    {
      key: 'income',
      labelKey: 'finance.summary.income',
      icon: BiSolidPlusCircle,
      accent: 'green',
      value: revenue ? revenue.income.total : null,
    },
    {
      key: 'expenses',
      labelKey: 'finance.summary.expenses',
      icon: BiSolidMinusCircle,
      accent: 'orange',
      value: revenue ? revenue.expenses.total : null,
    },
    {
      key: 'net',
      labelKey: 'finance.summary.net',
      icon: BiWallet,
      accent: 'indigo',
      value: revenue ? revenue.net : null,
    },
  ];

  return (
    <section
      className="fa-revenue-summary"
      aria-labelledby="finance-summary-heading"
      aria-busy={status === 'loading'}
    >
      <h2
        id="finance-summary-heading"
        ref={headingRef}
        tabIndex={-1}
        className="fa-visually-hidden"
      >
        {t('finance.summary.heading')}
      </h2>

      {status === 'error' ? (
        <div className="fa-revenue-summary__error" role="status">
          <p>{t('finance.summary.loadError')}</p>
          <button
            type="button"
            ref={retryButtonRef}
            className="fa-btn fa-btn--secondary"
            onClick={handleRetryClick}
          >
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <>
          <div className="fa-revenue-summary__cards">
            {cards.map((card) => {
              const Icon = card.icon;
              const showSkeleton = status === 'loading' || card.value == null;
              return (
                <div key={card.key} className="fa-revenue-kpi">
                  <span
                    className={`fa-revenue-kpi__icon-chip fa-revenue-kpi__icon-chip--${card.accent}`}
                    aria-hidden="true"
                  >
                    <Icon />
                  </span>
                  <span className="fa-revenue-kpi__label">{t(card.labelKey)}</span>
                  {showSkeleton ? (
                    <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                  ) : (
                    <span
                      className={`fa-revenue-kpi__value fa-ltr-isolate${
                        card.key === 'net' && netNegative ? ' fa-revenue-kpi__value--negative' : ''
                      }`}
                    >
                      <bdi>{formatCurrencyEGP(Number(card.value), i18n.language)}</bdi>
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="fa-revenue-breakdown">
            <div className="fa-revenue-breakdown__column">
              <h3 className="fa-revenue-breakdown__title">{t('finance.summary.income')}</h3>
              <dl className="fa-revenue-breakdown__list">
                <div className="fa-revenue-breakdown__row">
                  <dt>{t('finance.summary.tuitionPayments')}</dt>
                  <dd className="fa-ltr-isolate">
                    {revenue ? (
                      <bdi>
                        {formatCurrencyEGP(Number(revenue.income.tuitionPaid), i18n.language)}
                      </bdi>
                    ) : (
                      <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                    )}
                  </dd>
                </div>
                <div className="fa-revenue-breakdown__row">
                  <dt>{t('finance.summary.downPayments')}</dt>
                  <dd className="fa-ltr-isolate">
                    {revenue ? (
                      <bdi>
                        {formatCurrencyEGP(Number(revenue.income.downPayments), i18n.language)}
                      </bdi>
                    ) : (
                      <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                    )}
                  </dd>
                </div>
                <div className="fa-revenue-breakdown__row">
                  <dt>{t('finance.summary.examPayments')}</dt>
                  <dd className="fa-ltr-isolate">
                    {revenue ? (
                      <bdi>
                        {formatCurrencyEGP(Number(revenue.income.examPayments), i18n.language)}
                      </bdi>
                    ) : (
                      <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="fa-revenue-breakdown__column">
              <h3 className="fa-revenue-breakdown__title">{t('finance.summary.expenses')}</h3>
              <dl className="fa-revenue-breakdown__list">
                <div className="fa-revenue-breakdown__row">
                  <dt>{t('finance.summary.examCosts')}</dt>
                  <dd className="fa-ltr-isolate">
                    {revenue ? (
                      <bdi>
                        {formatCurrencyEGP(Number(revenue.expenses.examCosts), i18n.language)}
                      </bdi>
                    ) : (
                      <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                    )}
                  </dd>
                </div>
                <div className="fa-revenue-breakdown__row">
                  <dt>{t('finance.summary.schoolCosts')}</dt>
                  <dd className="fa-ltr-isolate">
                    {revenue ? (
                      <bdi>
                        {formatCurrencyEGP(Number(revenue.expenses.schoolCosts), i18n.language)}
                      </bdi>
                    ) : (
                      <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                    )}
                  </dd>
                </div>
                <div className="fa-revenue-breakdown__row">
                  <dt>{t('finance.summary.referralCosts')}</dt>
                  <dd className="fa-ltr-isolate">
                    {revenue ? (
                      <bdi>
                        {formatCurrencyEGP(Number(revenue.expenses.referralCosts), i18n.language)}
                      </bdi>
                    ) : (
                      <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                    )}
                  </dd>
                </div>
                <div className="fa-revenue-breakdown__row">
                  <dt>{t('finance.summary.otherExpenses')}</dt>
                  <dd className="fa-ltr-isolate">
                    {revenue ? (
                      <bdi>
                        {formatCurrencyEGP(Number(revenue.expenses.otherExpenses), i18n.language)}
                      </bdi>
                    ) : (
                      <span className="fa-revenue-kpi__skeleton-line" aria-hidden="true" />
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

interface FinanceLedgerTableProps {
  entries: LedgerEntry[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

/**
 * Money-flow ledger (17-finance-ux.md §3.4/§3.5) — a standard `DataTable` instance with
 * `meta={null}` (no pagination footer — the contract's `entries[]` is a flat, period-bounded
 * array, never paginated) and no search box (the period filter is the only scoping mechanism this
 * slice needs). Server ordering (`date` descending) is authoritative — no client sort control.
 */
function FinanceLedgerTable({ entries, loading, error, onRetry }: FinanceLedgerTableProps) {
  const { t, i18n } = useTranslation();
  const reversedPairKeys = useMemo(() => computeReversedPairKeys(entries), [entries]);

  const columns: DataTableColumn<LedgerEntry>[] = [
    {
      key: 'date',
      header: t('finance.ledger.columns.date'),
      render: (row) => <bdi>{formatDateOnly(row.date, i18n.language)}</bdi>,
    },
    {
      key: 'direction',
      header: t('finance.ledger.columns.direction'),
      render: (row) => (
        <span className="fa-ledger-direction-cell">
          <span aria-hidden="true" className="fa-ledger-direction-cell__glyph">
            {row.direction === 'income' ? '+' : '−'}
          </span>
          <StatusChip
            label={t(`finance.ledger.direction.${row.direction}`)}
            tone={row.direction === 'income' ? 'success' : 'danger'}
          />
        </span>
      ),
    },
    {
      key: 'amount',
      header: t('finance.ledger.columns.amount'),
      render: (row) => <LedgerAmountCell entry={row} language={i18n.language} />,
    },
    {
      key: 'source',
      header: t('finance.ledger.columns.source'),
      render: (row) => {
        const key = reversalPairKey(row);
        const reversed =
          row.entryType === 'exam_reversal' || (key != null && reversedPairKeys.has(key));
        return (
          <span className="fa-ledger-source-cell">
            <span>{row.sourceLabel}</span>
            {reversed && <StatusChip label={t('finance.ledger.reversedBadge')} tone="neutral" />}
          </span>
        );
      },
    },
    {
      key: 'related',
      header: t('finance.ledger.columns.related'),
      render: (row) => renderRelated(row),
    },
  ];

  return (
    <div className="fa-finance-ledger">
      <h2 className="fa-finance-ledger__title">{t('finance.ledger.title')}</h2>
      <DataTable
        columns={columns}
        rows={entries}
        rowKey={(row) => row.id}
        caption={t('finance.ledger.tableCaption')}
        meta={null}
        loading={loading}
        error={error}
        onRetry={onRetry}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        showExportButton={false}
        emptyState={
          <EmptyState
            icon={BiSolidInbox}
            title={t('finance.ledger.empty.title')}
            hint={t('finance.ledger.empty.hint')}
          />
        }
      />
    </div>
  );
}

/**
 * Overview tab (`/finance`, tab 1, 17-finance-ux.md §3.2–§3.5) — the Cairo period filter bar
 * drives TWO parallel, independently-retryable fetches (`GET /finance/revenue` +
 * `GET /finance/ledger`) with the identical resolved-period params, so the summary and the ledger
 * can never visually disagree (AC11) while a failure in either never blanks the other (mirrors
 * the dashboard's per-widget independent-failure convention).
 *
 * §10.2 (non-blocking decision): reuses the already-shipped `DashboardFilterBar` component
 * DIRECTLY (not a copy, not a new `FinancePeriodFilterBar`) — its props/i18n copy ("Today / This
 * month / This year / Custom", "Period filter", "From"/"To"/"Apply") are already generic, not
 * dashboard-specific wording, and `FinancePeriod`/`DashboardSummaryPeriod` share the exact same
 * string-literal union by design (`finance.enum.ts`), so no cast or duplication is needed. This is
 * the lowest-drift-risk resolution of the two options the design doc offers.
 */
export function FinanceOverviewTab() {
  const { t, i18n } = useTranslation();

  const [selectedPeriod, setSelectedPeriod] = useState<FinancePeriod>(FINANCE_DEFAULT_PERIOD);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [filterFieldErrors, setFilterFieldErrors] = useState<DashboardFilterBarFieldErrors>({});
  const [filterGeneralError, setFilterGeneralError] = useState<string | null>(null);

  const paramsRef = useRef<FinancePeriodQuery>({ period: FINANCE_DEFAULT_PERIOD });

  const [revenue, setRevenue] = useState<RevenueResponse | null>(null);
  const [revenueStatus, setRevenueStatus] = useState<WidgetFetchStatus>('loading');
  const [revenueToken, setRevenueToken] = useState(0);
  const revenueReqSeq = useRef(0);

  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(true);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [ledgerToken, setLedgerToken] = useState(0);
  const ledgerReqSeq = useRef(0);

  function applyPeriodQueryError(err: unknown) {
    const apiError = ApiError.fromUnknown(err);
    const fromDetail = apiError.details.find((d) => d.field === 'from');
    const toDetail = apiError.details.find((d) => d.field === 'to');
    if (fromDetail || toDetail) {
      setFilterFieldErrors((prev) => ({
        from: fromDetail?.message ?? prev.from,
        to: toDetail?.message ?? prev.to,
      }));
    } else if (apiError.code === 'NETWORK_ERROR') {
      setFilterGeneralError(t('finance.filterBar.errors.network'));
    }
  }

  useEffect(() => {
    const reqId = ++revenueReqSeq.current;
    setRevenueStatus('loading');
    getRevenue(paramsRef.current)
      .then((res) => {
        if (reqId !== revenueReqSeq.current) return;
        setRevenue(res);
        setRevenueStatus('loaded');
      })
      .catch((err) => {
        if (reqId !== revenueReqSeq.current) return;
        applyPeriodQueryError(err);
        setRevenueStatus('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueToken]);

  useEffect(() => {
    const reqId = ++ledgerReqSeq.current;
    setLedgerLoading(true);
    setLedgerError(null);
    getLedger(paramsRef.current)
      .then((res) => {
        if (reqId !== ledgerReqSeq.current) return;
        setLedgerEntries(res.entries);
      })
      .catch((err) => {
        if (reqId !== ledgerReqSeq.current) return;
        applyPeriodQueryError(err);
        setLedgerError(t('finance.ledger.loadError'));
      })
      .finally(() => {
        if (reqId !== ledgerReqSeq.current) return;
        setLedgerLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerToken]);

  function refetchBoth() {
    setRevenueToken((v) => v + 1);
    setLedgerToken((v) => v + 1);
  }

  function handlePillClick(period: FinancePeriod) {
    setFilterFieldErrors({});
    setFilterGeneralError(null);
    setSelectedPeriod(period);
    if (period === 'custom') {
      // Reveals the from/to inputs only — refetches on explicit Apply (§2.5's "one control drives
      // two fetches" still holds; Custom just needs its bounds first).
      return;
    }
    paramsRef.current = { period };
    refetchBoth();
  }

  function handleApplyCustomRange() {
    const from = customFrom.trim();
    const to = customTo.trim();

    const requiredErrors: DashboardFilterBarFieldErrors = {};
    if (!from) requiredErrors.from = t('finance.filterBar.errors.fromRequired');
    if (!to) requiredErrors.to = t('finance.filterBar.errors.toRequired');
    if (Object.keys(requiredErrors).length > 0) {
      setFilterFieldErrors(requiredErrors);
      return;
    }

    setFilterFieldErrors({});
    setFilterGeneralError(null);
    paramsRef.current = { period: 'custom', from, to };
    refetchBoth();
  }

  const resolvedPeriod = revenue?.resolvedPeriod ?? null;
  const resolvedPeriodText = resolvedPeriod
    ? t('finance.summary.resolvedPeriodLabel', {
        from: isolate(formatDateOnly(resolvedPeriod.from, i18n.language)),
        to: isolate(formatDateOnly(resolvedPeriod.to, i18n.language)),
      })
    : null;

  return (
    <div className="fa-finance-overview">
      <DashboardFilterBar
        periods={FINANCE_PERIODS}
        selectedPeriod={selectedPeriod}
        onPillClick={handlePillClick}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={(value) => {
          setCustomFrom(value);
          setFilterFieldErrors((prev) => ({ ...prev, from: undefined }));
        }}
        onCustomToChange={(value) => {
          setCustomTo(value);
          setFilterFieldErrors((prev) => ({ ...prev, to: undefined }));
        }}
        onApply={handleApplyCustomRange}
        isBusy={revenueStatus === 'loading' || ledgerLoading}
        fieldErrors={filterFieldErrors}
        generalError={filterGeneralError}
      />

      {resolvedPeriodText && (
        <p className="fa-finance-overview__resolved-period" aria-live="polite">
          {resolvedPeriodText}
        </p>
      )}

      <FinanceRevenueSummary
        revenue={revenue}
        status={revenueStatus}
        onRetry={() => setRevenueToken((v) => v + 1)}
      />

      <FinanceLedgerTable
        entries={ledgerEntries}
        loading={ledgerLoading}
        error={ledgerError}
        onRetry={() => setLedgerToken((v) => v + 1)}
      />
    </div>
  );
}
